import React, { useState } from 'react';
import { company, paymentPlan } from '../../mock';
import { Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function VideoPayment() {
  const [play, setPlay] = useState(false);
  const [brochure, setBrochure] = useState({ name: '', phone: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const requestBrochure = async (e) => {
    e.preventDefault();
    if (!brochure.name || !brochure.phone) {
      toast({ title: 'Add your name and phone', description: 'We need both to send the brochure.' });
      return;
    }
    try {
      setBusy(true);
      await axios.post(`${API}/brochure-requests`, brochure);
      toast({ title: 'Brochure on its way', description: 'We\u2019ll email you within 15 minutes.' });
      setBrochure({ name: '', phone: '', email: '' });
      setOpen(false);
    } catch (err) {
      toast({ title: 'Something went wrong', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-[#f6f1e7] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative aspect-video overflow-hidden bg-[#0f1a14] group">
          {play ? (
            <iframe
              className="w-full h-full"
              src={`${company.youtube}?autoplay=1`}
              title="Ewen Park video"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          ) : (
            <>
              <img src="https://images.unsplash.com/photo-1697653664649-936ed36b3c8c?w=1400&q=80" alt="video cover" className="h-full w-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
              <button onClick={() => setPlay(true)} className="absolute inset-0 grid place-items-center">
                <div className="h-20 w-20 rounded-full bg-[#c9a56a] grid place-items-center transition-transform hover:scale-110">
                  <Play className="h-8 w-8 text-[#0f1a14] ml-1" />
                </div>
              </button>
              <div className="absolute bottom-4 left-4 text-white">
                <div className="text-xs uppercase tracking-widest text-[#c9a56a]">Watch</div>
                <div className="font-serif text-lg">Himalayan View · The Ewen Park</div>
              </div>
            </>
          )}
        </div>
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#8a6b3a]" />
            <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">Cost & Payment Plan</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0f1a14] leading-[1.1]">Simple, transparent and buyer‑first.</h2>
          <p className="text-[#0f1a14]/70 mt-4 max-w-lg">Construction cost varies with design and requirements. Land payments are structured across five predictable milestones.</p>
          <div className="mt-8 divide-y divide-[#0f1a14]/10 border-y border-[#0f1a14]/10">
            {paymentPlan.map((p) => (
              <div key={p.step} className="py-5 grid grid-cols-12 gap-4 items-center hover:bg-white/60 transition-colors px-3">
                <div className="col-span-1 font-serif text-2xl text-[#8a6b3a]">{p.step}</div>
                <div className="col-span-5">
                  <div className="font-medium text-[#0f1a14]">{p.title}</div>
                  <div className="text-xs text-[#0f1a14]/60">{p.note}</div>
                </div>
                <div className="col-span-6 text-right font-serif text-2xl text-[#0f1a14]">{p.percent}</div>
              </div>
            ))}
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="mt-8 bg-[#0f1a14] text-white hover:bg-[#1c2e26] rounded-none px-8 py-6 tracking-wider">Request Brochure</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-white">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#0f1a14]">Request Brochure</DialogTitle>
                <DialogDescription className="text-[#0f1a14]/60">Enter your details and we&apos;ll email the brochure within 15 minutes.</DialogDescription>
              </DialogHeader>
              <form onSubmit={requestBrochure} className="space-y-3 mt-2">
                <Input placeholder="Full name" className="rounded-none h-12 border-[#0f1a14]/15" value={brochure.name} onChange={(e) => setBrochure({ ...brochure, name: e.target.value })} />
                <Input placeholder="Phone number" className="rounded-none h-12 border-[#0f1a14]/15" value={brochure.phone} onChange={(e) => setBrochure({ ...brochure, phone: e.target.value })} />
                <Input placeholder="Email (optional)" type="email" className="rounded-none h-12 border-[#0f1a14]/15" value={brochure.email} onChange={(e) => setBrochure({ ...brochure, email: e.target.value })} />
                <Button type="submit" disabled={busy} className="w-full bg-[#0f1a14] text-white hover:bg-[#1c2e26] rounded-none py-6 tracking-wider">
                  {busy ? 'Sending...' : 'Send Me the Brochure'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
}
